import React, { useState, useEffect } from "react";
import { API } from "../../App";
import { Camera, X } from "lucide-react";

const tokenStr = () => localStorage.getItem("token");
const fmtDate = (d) => {
  try {
    return new Date(d).toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" });
  } catch {
    return d;
  }
};

export function DiaryPhotos({ siteId, date, siteName }) {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    if (!siteId || !date) {
      setPhotos([]);
      return;
    }
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(
          `${API}/sites/${siteId}/photos?diary_only=true&on_date=${date}`,
          { headers: { Authorization: `Bearer ${tokenStr()}` } }
        );
        if (res.ok && active) setPhotos(await res.json());
        else if (active) setPhotos([]);
      } catch {
        if (active) setPhotos([]);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [siteId, date]);

  const img = (id, thumb = true) =>
    `${API}/photos/${id}/file?${thumb ? "thumb=true&" : ""}auth=${tokenStr()}`;

  return (
    <div className="border-2 border-slate-700 rounded-lg overflow-hidden mt-4" data-testid="diary-photos-card">
      <div className="bg-slate-700 text-white p-2 flex items-center justify-between">
        <span className="text-xs font-bold tracking-wide flex items-center gap-2">
          <Camera className="w-3.5 h-3.5" /> SITE DIARY PHOTOS
        </span>
        <span className="text-[10px] bg-white/20 px-2.5 py-0.5 rounded-full" data-testid="diary-photos-count">
          {photos.length} photo{photos.length === 1 ? "" : "s"} for this day
        </span>
      </div>
      <div className="p-3 bg-white">
        <p className="text-[11px] text-slate-500 mb-3">
          Photos marked “Attach to diary” for{" "}
          <b>{siteName || "this site"}</b> on <b>{fmtDate(date)}</b> — view only. Tap to enlarge.
        </p>

        {loading ? (
          <p className="text-xs text-slate-400 py-4">Loading…</p>
        ) : photos.length === 0 ? (
          <p className="text-xs text-slate-400 py-4" data-testid="diary-photos-empty">
            No diary photos for this day. Mark a photo “Attach to diary” on the Site Photos screen to record it here.
          </p>
        ) : (
          <div className="flex gap-3 flex-wrap">
            {photos.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => setLightbox(p.id)}
                className="relative w-28 h-28 rounded-xl overflow-hidden bg-slate-100 group"
                data-testid={`diary-photo-${p.id}`}
              >
                <img src={img(p.id)} alt={p.area_tag || ""} className="w-full h-full object-cover transition-transform group-hover:scale-105" />
                {p.area_tag && (
                  <span className="absolute bottom-0 left-0 right-0 bg-black/55 text-white text-[10px] px-1.5 py-0.5 truncate">
                    {p.area_tag}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {lightbox && (
        <div
          className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
          data-testid="diary-photo-lightbox"
        >
          <button className="absolute top-4 right-4 text-white/80 hover:text-white" onClick={() => setLightbox(null)}>
            <X className="w-7 h-7" />
          </button>
          <img src={img(lightbox, false)} alt="" className="max-w-full max-h-full rounded-lg" />
        </div>
      )}
    </div>
  );
}
